import {correctNumber} from '../common/formatters';
import {isAsecurityKey} from '../components/security/aspreader-security';
import {SelectItem} from '../components/select-input/select-item';
import {isSmartSecurityKey} from '../components/smart-security';
import type {Order} from '../models/order';
import {Report} from '../models/report';
import {SecurityKey} from '../models/security-key';
import {OrdType} from './order-type';
import {TimeInForce} from './time-in-force';

export enum OrderAlgoType {
    GENERAL          = 'GENERAL',
    SMART            = 'SMART',
    TWAP             = 'TWAP',
    SNIPER           = 'SNIPER',
    ARTIFICIAL_LIMIT = 'ARTIFICIAL_LIMIT',
    AUTOSPREADER     = 'AUTOSPREADER'
}

export const ORDER_ALGO_TYPE_NAMING: Record<OrderAlgoType, string> = {
    [OrderAlgoType.GENERAL]:          'enums.order-algo-type.value_general',
    [OrderAlgoType.SMART]:            'enums.order-algo-type.value_smart',
    [OrderAlgoType.TWAP]:             'enums.order-algo-type.value_twap',
    [OrderAlgoType.SNIPER]:           'enums.order-algo-type.value_sniper',
    [OrderAlgoType.ARTIFICIAL_LIMIT]: 'enums.order-algo-type.value_artificial_limit',
    [OrderAlgoType.AUTOSPREADER]:     'enums.order-algo-type.value_autospreader'
};

export const ORDER_ALGO_TYPE_MENU: Array<SelectItem<OrderAlgoType>> = [
    OrderAlgoType.GENERAL,
    OrderAlgoType.SMART,
    OrderAlgoType.TWAP,
    OrderAlgoType.SNIPER, // Not available in HG and Tradias
    // OrderAlgoType.ARTIFICIAL_LIMIT, // Available in Tradias only
    OrderAlgoType.AUTOSPREADER
].map(value => ({
    label: ORDER_ALGO_TYPE_NAMING[value],
    value
}));

const ORDER_ALGO_TYPES_WITH_CHILDREN: OrderAlgoType[] = [
    OrderAlgoType.SMART,
    OrderAlgoType.TWAP,
    OrderAlgoType.SNIPER,
    OrderAlgoType.ARTIFICIAL_LIMIT,
    OrderAlgoType.AUTOSPREADER
];

export interface OrderInputParamsByAlgoType {
    orderType?: OrdType;
    isOrderTypeReadonly?: boolean;
    timeInForce?: TimeInForce;
    isTimeInForceReadonly?: boolean;
}

/**
 * Defines order parameters depending on algoType.
 */
const ORDER_INPUT_PARAMS_BY_ALGO_TYPE: Partial<Record<OrderAlgoType, OrderInputParamsByAlgoType>> = {
    [OrderAlgoType.TWAP]:             {
        timeInForce:           TimeInForce.GTC,
        isTimeInForceReadonly: true
    },
    [OrderAlgoType.SNIPER]:           {
        orderType:             OrdType.LIMIT,
        isOrderTypeReadonly:   true,
        timeInForce:           TimeInForce.GTC,
        isTimeInForceReadonly: true
    },
    [OrderAlgoType.ARTIFICIAL_LIMIT]: {
        orderType:           OrdType.LIMIT,
        isOrderTypeReadonly: true
    },
    [OrderAlgoType.AUTOSPREADER]:     {
        orderType:             OrdType.LIMIT,
        isOrderTypeReadonly:   true,
        timeInForce:           TimeInForce.GTC,
        isTimeInForceReadonly: true
    }
};

export const getOrderAlgoTypeMenuBySecurityKey = (securityKey: SecurityKey): Array<SelectItem<OrderAlgoType>> =>
    isAsecurityKey(securityKey)
        ? ORDER_ALGO_TYPE_MENU.filter(item => item.value === OrderAlgoType.AUTOSPREADER)
        : isSmartSecurityKey(securityKey)
            ? ORDER_ALGO_TYPE_MENU.filter(item =>
                item.value !== OrderAlgoType.GENERAL
                && item.value !== OrderAlgoType.AUTOSPREADER
            )
            : ORDER_ALGO_TYPE_MENU.filter(item =>
                item.value !== OrderAlgoType.SMART
                && item.value !== OrderAlgoType.AUTOSPREADER
            );

export const getAvailableOrderAlgoTypesSecurityKey = (securityKey: SecurityKey): OrderAlgoType[] =>
    getOrderAlgoTypeMenuBySecurityKey(securityKey).map(item => item.value);

export const correctOrderAlgoType = (securityKey: SecurityKey, algoType: OrderAlgoType): OrderAlgoType => {
    const availableAlgoTypes = getAvailableOrderAlgoTypesSecurityKey(securityKey);

    return availableAlgoTypes.includes(algoType)
        ? algoType
        : availableAlgoTypes[0];
};

/**
 * Converts {@link Order.algoType} to a valid {@link OrderAlgoType}.
 */
export const parseAlgoTypeFromOrder = (algoTypeFromOrder: OrderAlgoType): OrderAlgoType =>
    algoTypeFromOrder || OrderAlgoType.GENERAL;

/**
 * Returns the value, that should be sent in order to API.
 */
export const prepareAlgoTypeForOrder = (algoType: OrderAlgoType): OrderAlgoType =>
    algoType === OrderAlgoType.GENERAL
        ? undefined
        : algoType;

export const isOrderChildOfComplexAlgo = (orderOrReport: Order | Report): boolean =>
    !!orderOrReport.algoOrigId;

export const isAlgoOrderWithChildren = (orderOrReport: Order | Report): boolean => {
    const algoType = parseAlgoTypeFromOrder(orderOrReport.algoType);

    return ORDER_ALGO_TYPES_WITH_CHILDREN.includes(algoType);
};

export const getOrderInputParamsByAlgoType = (algoType: OrderAlgoType): OrderInputParamsByAlgoType =>
    ORDER_INPUT_PARAMS_BY_ALGO_TYPE[parseAlgoTypeFromOrder(algoType)]
    || {};

/**
 * Common Algo helpers
 */
const getNearestDurationUnit = (value: number, durationMenu: Array<SelectItem<number>>): number => {
    let unit: number = 1;

    for (let i = durationMenu.length - 1; i >= 0; i--) {
        const currentUnit = durationMenu[i].value;

        if (value % currentUnit === 0) {
            unit = currentUnit;
            break;
        }
    }

    return unit;
};

const correctDuration = (value: number, unit: number, min: number, max: number) => {
    const valueSec = (value || 1) * unit;
    const correctStepDelaySec = correctNumber(valueSec, min, max, 1);

    return Math.round(correctStepDelaySec / unit);
};

/**
 * TWAP algo
 */
export const TWAP_STEP_DELAY_UNIT_MENU: Array<SelectItem<number>> = [
    {
        label: 'enums.order-algo-type.duration_unit_seconds',
        value: 1
    },
    {
        label: 'enums.order-algo-type.duration_unit_minutes',
        value: 60
    },
    {
        label: 'enums.order-algo-type.duration_unit_hours',
        value: 60 * 60
    }
];

export const TWAP_STEP_DELAY_MIN_SEC = 5;
export const TWAP_STEP_DELAY_MAX_SEC = 3600; // 1 hour

export const getNearestTwapStepDelayUnit = (value: number): number =>
    getNearestDurationUnit(value, TWAP_STEP_DELAY_UNIT_MENU);

export const correctTwapStepDelay = (value: number, unit: number) =>
    correctDuration(value, unit, TWAP_STEP_DELAY_MIN_SEC, TWAP_STEP_DELAY_MAX_SEC);

/**
 * Sniper algo
 */
export const SNIPER_DELAY_UNIT_MENU: Array<SelectItem<number>> = [
    {
        label: 'enums.order-algo-type.duration_unit_milliseconds',
        value: 1
    },
    {
        label: 'enums.order-algo-type.duration_unit_seconds',
        value: 1000
    },
    {
        label: 'enums.order-algo-type.duration_unit_minutes',
        value: 60 * 1000
    },
    {
        label: 'enums.order-algo-type.duration_unit_hours',
        value: 60 * 60 * 1000
    }
];

export const SNIPER_DELAY_MIN_MS = 2 * 1000; // 2 seconds
export const SNIPER_DELAY_MAX_MS = 3600 * 1000; // 1 hour

export const getNearestSniperDelayUnit = (value: number): number =>
    getNearestDurationUnit(value, SNIPER_DELAY_UNIT_MENU);

export const correctSniperDelay = (value: number, unit: number) =>
    correctDuration(value, unit, SNIPER_DELAY_MIN_MS, SNIPER_DELAY_MAX_MS);
